/**
 * PER-ASSET DETAIL (Wallet depth, FR-U-WAL-002). One asset in the wallet: its balance, its KES value,
 * the actions for it, and its own slice of history.
 */

import { useCallback, useEffect, useState } from 'react';
import { fmtDateShort } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { api, type Balance, type RateBoard } from '@/lib/api';
import { ErrorState } from '@/components/states';
import { Reveal } from '@/components/motion';
import { useSession } from '@/lib/session-context';

type History = Awaited<ReturnType<typeof api.history>>['transactions'];

const NAMES: Record<string, string> = {
  KES: 'Kenyan Shilling',
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  USDT: 'Tether',
  USDC: 'USD Coin',
  SOL: 'Solana',
  XRP: 'XRP',
  BNB: 'BNB',
};

export default function AssetDetail() {
  const { me } = useSession();
  const params = useParams();
  const code = (params.code ?? '').toUpperCase();

  const [balance, setBalance] = useState<Balance | null>(null);
  const [board, setBoard] = useState<RateBoard | null>(null);
  const [history, setHistory] = useState<History>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!me) return;
    try {
      const [w, r, h] = await Promise.all([
        api.wallet(me.participant_id),
        api.rates().catch(() => null),
        api.history(me.participant_id),
      ]);
      setBalance(w.balances.find((b) => b.asset === code) ?? null);
      setBoard(r);
      setHistory(h.transactions.filter((t) => t.asset === code));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [me, code]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body="We could not load this asset." onRetry={() => void load()} />;
  if (loading) return <div className="skeleton h-40 animate-shimmer rounded-xl" />;

  const kesRate = board?.rates.find((r) => r.asset === code)?.kes_per_unit;
  const kesValue =
    balance && kesRate ? Number(balance.available.replace(/,/g, '')) * Number(kesRate) : null;
  const isFiat = code === 'KES';

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link
        to="/wallet"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Wallet
      </Link>

      <Reveal>
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
          />
          <div className="relative">
            <p className="text-sm text-white/70">{NAMES[code] ?? code}</p>
            <p className="tabular mt-1 text-3xl font-semibold tracking-tight">
              {balance?.available ?? '0'}{' '}
              <span className="text-lg font-medium text-white/70">{code}</span>
            </p>
            {kesValue !== null && !isFiat ? (
              <p className="mt-1 text-sm text-white/70">
                ≈ KES {kesValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </p>
            ) : null}
            {balance && balance.locked !== '0.00' && balance.locked.replace(/[0.]/g, '') !== '' ? (
              <p className="mt-1 text-sm text-white/70">
                {balance.locked} {code} locked
              </p>
            ) : null}

            <div className="mt-5 grid grid-cols-3 gap-2">
              <Act href="/transact" label="Send" icon="M6 18 18 6m0 0h-8m8 0v8" />
              <Act href="/receive" label="Receive" icon="M18 6 6 18m0 0h8m-8 0v-8" />
              <Act
                href="/transact"
                label="Convert"
                icon="M7.5 7.5h11m0 0-3-3m3 3-3 3M16.5 16.5h-11m0 0 3 3m-3-3 3-3"
              />
            </div>
          </div>
        </section>
      </Reveal>

      <section>
        <h2 className="mb-2 font-semibold text-ink">{code} activity</h2>
        {history.length === 0 ? (
          <p className="rounded-card border border-border bg-surface p-8 text-center text-sm text-ink-muted shadow-card">
            No {code} movements yet.
          </p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {history.map((t) => {
              const incoming = t.direction === 'in';
              return (
                <li key={t.id}>
                  <Link
                    to={`/activity/${t.correlationId}`}
                    className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${incoming ? 'bg-accent/12 text-accent' : 'bg-primary/10 text-brand'}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d={incoming ? 'M18 6 6 18m0 0h8m-8 0v-8' : 'M6 18 18 6m0 0h-8m8 0v8'}
                        />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">
                        {incoming ? 'Received' : 'Sent'}
                      </p>
                      <p className="truncate text-xs text-ink-subtle">
                        {fmtDateShort(t.at)} &middot; {t.state}
                      </p>
                    </div>
                    <span
                      className={`tabular shrink-0 text-sm font-semibold ${incoming ? 'text-accent' : 'text-ink'}`}
                    >
                      {incoming ? '+' : '−'} {t.amount}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function Act({ href, label, icon }: { href: string; label: string; icon: string }) {
  return (
    <Link
      to={href}
      className="flex flex-col items-center gap-1.5 rounded-lg bg-white/10 py-2.5 text-white backdrop-blur transition hover:bg-white/20"
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={icon} />
      </svg>
      <span className="text-micro font-medium">{label}</span>
    </Link>
  );
}
